import React from "react";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import toast from "react-hot-toast";

import { BenefitCategory } from "../../models/benefits";
import {
  useGetBenefitCategoriesQuery,
  useDeleteBenefitCategoryMutation,
  useUpdateBenefitCategoryMutation,
} from "../../store/services/benefits";
import { getBenefitCategoryLucideIcon } from "../../utils/constants/benefits-constants";
import {
  getAdminFacingApiErrorMessage,
  getApiErrorCode,
  getApiErrorMessage,
} from "../../utils/functions/api-errors";
import { AppDialog, DetailField, DetailGrid, PageHeader, RowActions, StatusBadge, TenantGateBanner, emptyMessage, useTenantGate, rowActionsTableStyle } from "../../components/ui";
import { BenefitCategoryForm } from "./BenefitCategoryForm";

// ABM de categorías de beneficios (BN-037). MT-011: las categorías viven dentro
// de un tenant, así que la pantalla necesita uno concreto para listar y crear.
// Un SUPER_ADMIN en scope global ("Todos") lo fija en el selector del header;
// antes el backend caía al tenant del admin y la categoría terminaba en el
// tenant equivocado (después: BENEFIT_CATEGORY_NOT_FOUND al asociarla).

type CategoryDialogState =
  | { mode: "create" }
  | { mode: "edit"; category: BenefitCategory };

const CATEGORY_HAS_PARTNERS_TOAST =
  "No se puede eliminar: hay partners asociados. Reasignales otra categoría o quitáles esta antes de borrar.";

const getCategoryHasPartnersToast = (error: unknown): string => {
  const raw = getApiErrorMessage(error) ?? "";
  const match = raw.match(/(\d+)\s+partner/i);
  const count = match ? Number(match[1]) : NaN;
  if (!Number.isFinite(count) || count <= 0) {
    return CATEGORY_HAS_PARTNERS_TOAST;
  }
  const noun = count === 1 ? "partner asociado" : "partners asociados";
  return `No se puede eliminar: hay ${count} ${noun}. Reasignales otra categoría o quitáles esta antes de borrar.`;
};

export const BenefitCategories = () => {
  const tenantGate = useTenantGate();
  const tenantId = tenantGate.tenantId;

  const { data, isLoading, isFetching, refetch } = useGetBenefitCategoriesQuery(
    { tenantId },
    { skip: !tenantGate.allowed }
  );

  const [updateCategory] = useUpdateBenefitCategoryMutation();
  const [deleteCategory, { isLoading: isDeleting }] =
    useDeleteBenefitCategoryMutation();

  const [dialogState, setDialogState] = React.useState<
    CategoryDialogState | undefined
  >();
  const [categoryToDelete, setCategoryToDelete] = React.useState<
    BenefitCategory | undefined
  >();
  const [categoryToView, setCategoryToView] = React.useState<
    BenefitCategory | undefined
  >();
  const [isSavingOrder, setIsSavingOrder] = React.useState(false);
  const [togglingId, setTogglingId] = React.useState<string | undefined>();

  const sortedCategories = React.useMemo(
    () =>
      data ? [...data].sort((a, b) => a.sortOrder - b.sortOrder) : [],
    [data]
  );

  // Copia local para feedback instantáneo del drag & drop; se resincroniza con
  // el server salvo mientras se está persistiendo un reordenamiento.
  const [rows, setRows] = React.useState<BenefitCategory[]>([]);

  React.useEffect(() => {
    if (!isSavingOrder) {
      setRows(sortedCategories);
    }
  }, [sortedCategories, isSavingOrder]);

  const hideDialog = () => setDialogState(undefined);
  const hideDeleteDialog = () => setCategoryToDelete(undefined);

  const handleRowReorder = (e: DataTableRowReorderEvent<BenefitCategory[]>) => {
    const reordered = e.value.map((category, index) => ({
      ...category,
      sortOrder: index,
    }));
    setRows(reordered);

    const changed = e.value
      .map((category, index) => ({ category, sortOrder: index }))
      .filter(({ category, sortOrder }) => category.sortOrder !== sortOrder);

    if (changed.length === 0) {
      return;
    }

    setIsSavingOrder(true);
    Promise.all(
      changed.map(({ category, sortOrder }) =>
        updateCategory({ id: category.id, sortOrder, tenantId }).unwrap()
      )
    )
      .then(() => toast.success("Orden actualizado"))
      .catch((err) => {
        toast.error(
          getAdminFacingApiErrorMessage(err, "No se pudo actualizar el orden")
        );
        void refetch();
      })
      .finally(() => setIsSavingOrder(false));
  };

  const handleDeleteCategory = (category: BenefitCategory) => {
    deleteCategory({ id: category.id, tenantId })
      .unwrap()
      .then(() => toast.success("Categoría eliminada"))
      .catch((err) => {
        toast.error(
          getApiErrorCode(err) === "BENEFIT_CATEGORY_HAS_PARTNERS"
            ? getCategoryHasPartnersToast(err)
            : getAdminFacingApiErrorMessage(
                err,
                "No se pudo eliminar la categoría"
              )
        );
      })
      .finally(() => hideDeleteDialog());
  };

  const handleToggleActive = (category: BenefitCategory, isActive: boolean) => {
    setTogglingId(category.id);
    updateCategory({ id: category.id, isActive, tenantId })
      .unwrap()
      .then(() => {
        toast.success(isActive ? "Categoría activada" : "Categoría desactivada");
      })
      .catch((err) => {
        toast.error(
          getAdminFacingApiErrorMessage(
            err,
            "No se pudo actualizar el estado de la categoría"
          )
        );
      })
      .finally(() => setTogglingId(undefined));
  };

  // Column templates
  const iconBodyTemplate = (category: BenefitCategory) => {
    if (!category.iconName) return "-";
    const Icon = getBenefitCategoryLucideIcon(category.iconName);
    return (
      <div className="flex align-items-center gap-2">
        <Icon size={18} />
        <span className="text-color-secondary text-sm">{category.iconName}</span>
      </div>
    );
  };

  const colorBodyTemplate = (category: BenefitCategory) =>
    category.color ? (
      <div className="flex align-items-center gap-2">
        <span
          style={{
            display: "inline-block",
            width: "1.25rem",
            height: "1.25rem",
            borderRadius: "4px",
            border: "1px solid var(--surface-border)",
            backgroundColor: category.color,
          }}
        />
        <span className="text-color-secondary text-sm">{category.color}</span>
      </div>
    ) : (
      "-"
    );

  const activeBodyTemplate = (category: BenefitCategory) => (
    <InputSwitch
      checked={category.isActive}
      disabled={togglingId === category.id || !tenantGate.allowed}
      onChange={(e: InputSwitchChangeEvent) =>
        handleToggleActive(category, !!e.value)
      }
    />
  );

  const actionsBodyTemplate = (category: BenefitCategory) => (
    <RowActions
      actions={[
        { icon: "pi pi-eye", label: "Ver", severity: "info", onClick: () => setCategoryToView(category) },
        { icon: "pi pi-pencil", label: "Editar", onClick: () => setDialogState({ mode: "edit", category }) },
        { icon: "pi pi-trash", label: "Eliminar", severity: "danger", onClick: () => setCategoryToDelete(category) },
      ]}
    />
  );

  return (
    <div className="p-card gap-4 p-3">
      <PageHeader
        title="Categorías de beneficios"
        onCreate={() => setDialogState({ mode: "create" })}
        createLabel="Crear categoría"
      />

      {/* §6.4 / INC-05 — Antes había acá un dropdown local de tenant
          (TenantSectionPicker, ya dado de baja): el tercer mecanismo que INC-05 elimina.
          No se puede simplemente quitarlo y listar con scope global: el backend
          no soporta listar categorías sin tenant y cae en silencio al tenant del
          admin (ver el comentario MT-011 en store/services/benefits.ts). Así que
          se bloquea la pantalla con el motivo a la vista, en vez de mostrar
          datos del tenant equivocado. */}
      <TenantGateBanner action="ver y crear categorías" gate={tenantGate} />

      <DataTable
        style={rowActionsTableStyle("4.5rem", "10rem")}
        value={tenantGate.allowed ? rows : []}
        dataKey="id"
        stripedRows
        className="equal-columns-table"
        loading={isLoading || isDeleting || isSavingOrder || isFetching}
        emptyMessage={
          tenantGate.allowed
            ? emptyMessage("categorías")
            : "Elegí un tenant en el header para ver sus categorías"
        }
        reorderableRows={!isSavingOrder && !!tenantGate.allowed}
        onRowReorder={handleRowReorder}
      >
        <Column rowReorder style={{ width: "3.5rem", minWidth: "3.5rem" }} />
        <Column field="name" header="Nombre" style={{ minWidth: "14rem" }} />
        <Column
          header="Icono"
          body={iconBodyTemplate}
          style={{ minWidth: "12rem" }}
        />
        <Column
          header="Color"
          body={colorBodyTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Activa"
          body={activeBodyTemplate}
          alignHeader="center"
          bodyClassName="text-center"
        />
        <Column
          header={<span className="row-actions-label">Acciones</span>}
          // Bloque contra el borde derecho con el mismo margen que la primera columna
          // a la izquierda; título e íconos centrados dentro del bloque.
          headerClassName="row-actions-th"
          // 3 íconos (10rem) + margen derecho (4.5rem) + padding izquierdo (1rem):
          // las demás columnas se reparten el resto en partes iguales.
          style={{ width: "15.5rem" }}
          bodyClassName="row-actions-cell"
          body={actionsBodyTemplate}
          exportable={false}
        />
      </DataTable>

      <AppDialog
        variant="detail"
        visible={!!categoryToView}
        onHide={() => setCategoryToView(undefined)}
        header="Detalle de la categoría"
      >
        {categoryToView && (
          <DetailGrid>
            <DetailField label="Nombre">{categoryToView.name}</DetailField>
            <DetailField label="Icono">{iconBodyTemplate(categoryToView)}</DetailField>
            <DetailField label="Color">{colorBodyTemplate(categoryToView)}</DetailField>
            <DetailField label="Estado">
              <StatusBadge
                label={categoryToView.isActive ? "Activa" : "Inactiva"}
                tone={categoryToView.isActive ? "success" : "error"}
              />
            </DetailField>
          </DetailGrid>
        )}
      </AppDialog>

      {/* Alta / edición */}
      {/* Modal formulario (§6.3): los botones [Primario] + [Cancelar] los pone el
          propio formulario con <FormActions>, así Enter sigue guardando. Mismo
          ancho, padding y comportamiento que <AppDialog>. */}
      <Dialog
        visible={!!dialogState}
        style={{ width: "min(90vw, 520px)" }}
        contentStyle={{ padding: "var(--space-32) var(--space-40) var(--space-40)" }}
        draggable={false}
        header={
          dialogState?.mode === "edit" ? "Editar Categoría" : "Crear Categoría"
        }
        modal
        onHide={hideDialog}
      >
        {dialogState && (
          <BenefitCategoryForm
            category={
              dialogState.mode === "edit" ? dialogState.category : undefined
            }
            tenantId={tenantId}
            handleClose={hideDialog}
          />
        )}
      </Dialog>

      <AppDialog
        variant="destructive"
        visible={!!categoryToDelete}
        onHide={hideDeleteDialog}
        header="Eliminar categoría"
        primaryLabel="Eliminar Categoría"
        onPrimary={() => categoryToDelete && handleDeleteCategory(categoryToDelete)}
        loading={isDeleting}
      >
        <span>
          ¿Estás seguro de que querés eliminar la categoría{" "}
          <strong>{categoryToDelete?.name}</strong>?
          <br />
          <small className="text-color-secondary">
            No se puede eliminar si hay partners asociados. Reasignales otra
            categoría o quitáles esta antes de borrar.
          </small>
        </span>
      </AppDialog>
    </div>
  );
};
